import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useScanInput } from '../hooks/useScanInput';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useToast } from '../components/ui/Toast';
import { DataGrid, type Column } from '../components/ui/DataGrid';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { FolderTree, Search, X } from 'lucide-react';
import { inputClass } from '../components/ui/Input';
import { ArticleImage } from '../components/ui/ArticleImage';
import i18n from '../i18n';
import { formatEUR } from '../lib/format';

/**
 * J2.1 — the five catalogue search modes. ALL fires every one of them at once and is what the
 * box does by default; the narrow modes are the ones a seller reaches for deliberately.
 *
 * OEM and CROSS_REF (modes 4 and 5, added in M7) exist as their own filters because "everything
 * matching 7701207066" and "the parts that ARE 7701207066" are different questions: a garage
 * holding an OE number wants equivalents, not every product whose description contains the
 * digits. Both legitimately return several brands, and the list is deliberately not collapsed.
 */
type Mode = 'ALL' | 'TEXT' | 'EAN' | 'SKU' | 'OEM' | 'CROSS_REF';
const MODES: readonly Mode[] = ['ALL', 'TEXT', 'EAN', 'SKU', 'OEM', 'CROSS_REF'] as const;

interface Hit {
  id: string;
  sku: string;
  name: string;
  brand: string | null;
  ean: string | null;
  manufacturerRef: string | null;
  stock: number;
  /** Set when the part came into our catalogue from a supplier (products.main_supplier_id). */
  providedBy: string | null;
  /** The part's own photo (when TecDoc has one) and the brand logo, tried in that order. */
  imageUrl: string | null;
  brandImageUrl: string | null;
}

interface ProductProj {
  [k: string]: unknown;
}
interface Availability {
  productId: string;
  internalStock: number;
  /** Shelf locations, main first (feedback 3.8). */
  locations?: string[];
  price: {
    unitPriceHT: number;
    finalUnitPriceHT: number;
    unitPriceTTC: number;
    discountPct: number;
  } | null; // null when the product has a non-positive margin and can't be priced
  supplierAvailable: boolean;
  supplierLeadDays: number | null;
  // Live per-supplier detail. Empty for roles without SUPPLIER_COMPARE(_VIEW); `unitPriceHT` is
  // a purchase cost and only present for margin authority.
  supplierAvailability: {
    supplierCode: string;
    supplierName: string;
    supplierArticleNo: string | null;
    status: 'OK' | 'UNAVAILABLE' | 'CIRCUIT_OPEN' | 'TIMEOUT' | 'MANUAL_ENTRY' | 'API_ERROR';
    availableQty: number | null;
    leadDays: number | null;
    cutoffTime: string | null;
    cutoffPassed: boolean;
    manualEntry: boolean;
    bestPrice: boolean;
    unitPriceHT?: number | null;
  }[];
}

/** Distinguish "no stock" from "we couldn't ask" — a timed-out supplier is not an out-of-stock one. */
function supplierStatusLabel(
  status: Availability['supplierAvailability'][number]['status']
): string {
  return i18n.t(`catalog.supplierStatus.${status}`, { defaultValue: status });
}

/** Screens 8-9 — Catalog Search. One box hits every index (text/EAN/SKU/OEM/cross-ref) at
 *  once. Ctrl/Cmd+K focuses search; an EAN scan auto-routes to the exact-EAN fast path. */
export default function Catalog() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') ?? '');
  const [rows, setRows] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Hit | null>(null);
  const [mode, setMode] = useState<Mode>('ALL');
  // Skip the debounced search once after a programmatic q change (scan / deep-link),
  // so those keep their explicit mode instead of being overwritten by an ALL search.
  const skipDebounce = useRef(false);

  const search = async (text: string, m: Mode = 'ALL') => {
    setBrowsing(null);
    if (!text.trim()) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.get<Hit[]>(
        `/catalog/search?q=${encodeURIComponent(text)}&mode=${m}`
      );
      setRows(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  /** Universal groups browsable without a reference — oils, batteries, accessories (2026-10-13). */
  const [groups, setGroups] = useState<{ key: string; name: string; count: number }[]>([]);
  const [browsing, setBrowsing] = useState<string | null>(null);
  useEffect(() => {
    apiClient
      .get<{ key: string; name: string; count: number }[]>('/catalog/browse-groups')
      .then((r) => setGroups(r.data))
      .catch(() => setGroups([]));
  }, []);
  const browse = async (key: string) => {
    setBrowsing(key);
    skipDebounce.current = true;
    setQ('');
    setLoading(true);
    try {
      const { data } = await apiClient.get<Hit[]>(
        `/catalog/browse?group=${encodeURIComponent(key)}`
      );
      setRows(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  // The global navbar search navigates here with ?q=…; pick it up and run the search (also
  // covers deep links / refreshes). Keyed on the raw URL value so re-submitting fires again.
  const urlQ = searchParams.get('q') ?? '';
  useEffect(() => {
    if (urlQ) {
      skipDebounce.current = true;
      setQ(urlQ);
      void search(urlQ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQ]);

  // Live search: run automatically a short moment after the user stops typing,
  // so no Enter / Search-button click is needed.
  useEffect(() => {
    if (skipDebounce.current) {
      skipDebounce.current = false;
      return;
    }
    const t = setTimeout(() => void search(q, mode), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, mode]);

  useScanInput({
    onScan: (text) => {
      skipDebounce.current = true;
      setQ(text);
      void search(text, 'EAN');
    },
  });
  useKeyboardShortcuts({
    onSearch: () => document.getElementById('catalog-q')?.focus(),
    onConfirm: () => search(q, mode),
    onCancel: () => {
      setQ('');
      setRows([]);
    },
  });

  const columns: Column<Hit>[] = [
    {
      key: 'imageUrl',
      header: '',
      render: (r) => (
        <ArticleImage
          src={r.imageUrl}
          brandSrc={r.brandImageUrl}
          alt={r.brand ?? r.name}
          size={40}
        />
      ),
    },
    { key: 'sku', header: t('catalog.sku') },
    {
      key: 'name',
      header: t('common.name'),
      render: (r) => (
        <span>
          {r.name}
          {r.providedBy && (
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
              {t('catalog.via', { supplier: r.providedBy })}
            </span>
          )}
        </span>
      ),
    },
    { key: 'brand', header: t('common.brand') },
    { key: 'ean', header: 'EAN' },
    { key: 'manufacturerRef', header: t('catalog.mfrRef') },
    {
      key: 'stock',
      header: t('common.stock'),
      render: (r) => <span className={r.stock <= 0 ? 'text-destructive' : ''}>{r.stock}</span>,
    },
  ];

  return (
    <div>
      <PageHeader title={t('catalog.title')} description={t('catalog.description')} />
      <div className="mb-4 flex items-center gap-2 rounded-lg border bg-card p-3 shadow-sm">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            id="catalog-q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search(q, mode)}
            placeholder={t(`catalog.searchPlaceholder${mode === 'ALL' ? '' : `.${mode}`}`, {
              defaultValue: t('catalog.searchPlaceholder'),
            })}
            className={inputClass + ' pl-8'}
          />
        </div>
        <select
          aria-label={t('catalog.searchMode')}
          value={mode}
          onChange={(e) => setMode(e.target.value as Mode)}
          // `inputClass` carries `w-full`; appending `w-auto` does NOT override it (Tailwind
          // resolves conflicts by CSS order, not string order), and a full-width select squashed
          // the search box next to it. Drop the width utility and set an explicit one.
          className={`${inputClass.replace('w-full', '')} w-52 shrink-0`}
        >
          {MODES.map((m) => (
            <option key={m} value={m}>
              {t(`catalog.mode.${m}`)}
            </option>
          ))}
        </select>
        <Button size="sm" onClick={() => search(q, mode)}>
          <Search className="h-4 w-4" />
          {t('common.search')}
        </Button>
      </div>
      {groups.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs font-medium text-muted-foreground">
            {t('catalog.browseCategories')}
          </span>
          {groups.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => void browse(g.key)}
              aria-pressed={browsing === g.key}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                browsing === g.key ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent'
              }`}
            >
              <FolderTree className="h-3.5 w-3.5" />
              {g.name}
              <span className="tabular-nums text-muted-foreground">{g.count}</span>
            </button>
          ))}
        </div>
      )}
      <DataGrid<Hit>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={loading}
        empty={t('catalog.empty')}
        onRowClick={(r) => setSelected(r)}
      />
      {selected && (
        <CatalogDetailPanel
          id={selected.id}
          fallbackName={selected.name || selected.sku}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

/** Right-side slide-in showing the same detail as the catalogue detail page
 *  (identifiers + availability), so a row click previews in place. */
function CatalogDetailPanel({
  id,
  fallbackName,
  onClose,
}: {
  id: string;
  fallbackName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [product, setProduct] = useState<ProductProj | null>(null);
  const [avail, setAvail] = useState<Availability | null>(null);

  useEffect(() => {
    let alive = true;
    setProduct(null);
    setAvail(null);
    (async () => {
      try {
        const p = await apiClient.get<ProductProj>(`/catalog/products/${id}`);
        const a = await apiClient.get<Availability>(`/catalog/products/${id}/availability`);
        if (!alive) return;
        setProduct(p.data);
        setAvail(a.data);
      } catch (err) {
        showApiError(err);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, showApiError]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <aside className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2
            className="truncate text-lg font-semibold"
            title={String(product?.name ?? fallbackName)}
          >
            {String(product?.name ?? fallbackName)}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label={t('catalog.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {!product ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <>
              <section className="border rounded p-4 bg-card">
                <h3 className="font-semibold mb-2">{t('catalog.identifiers')}</h3>
                <dl className="grid grid-cols-2 gap-y-1 text-sm">
                  {Object.entries(product).map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="text-muted-foreground">{k}</dt>
                      <dd className="font-mono text-xs break-all">{String(v ?? '—')}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              {avail && (
                <section className="border rounded p-4 bg-card">
                  <h3 className="font-semibold mb-2">{t('catalog.availability')}</h3>
                  <div className="text-sm space-y-1">
                    <div>
                      {t('catalog.internalStock')} <strong>{avail.internalStock}</strong>
                    </div>
                    {/* Feedback 3.8 — where the part is, when it has a shelf. */}
                    {(avail.locations ?? []).length > 0 && (
                      <div>
                        {t('catalog.locations')}{' '}
                        <strong className="font-mono">{(avail.locations ?? []).join(' · ')}</strong>
                      </div>
                    )}
                    {avail.price ? (
                      <>
                        <div>
                          {t('catalog.saleHT')}{' '}
                          <strong>{formatEUR(avail.price.finalUnitPriceHT)}</strong>
                        </div>
                        <div>
                          {t('catalog.saleTTC')}{' '}
                          <strong>{formatEUR(avail.price.unitPriceTTC)}</strong>
                        </div>
                        {avail.price.discountPct > 0 && (
                          <div className="text-success">
                            {t('catalog.gridDiscount', { pct: avail.price.discountPct })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-warning">{t('catalog.priceUnavailable')}</div>
                    )}
                  </div>
                  {avail.supplierAvailability.length > 0 && (
                    <>
                      <h4 className="font-semibold mt-3 mb-1 text-sm">
                        {t('catalog.supplierAvailability')}
                      </h4>
                      <ul className="text-xs space-y-1">
                        {avail.supplierAvailability.map((s) => (
                          <li
                            key={s.supplierCode}
                            className={s.bestPrice ? 'text-success' : undefined}
                          >
                            <span className="font-mono">{s.supplierCode}</span> — {s.supplierName}
                            {s.bestPrice && <span className="ml-1">{t('catalog.best')}</span>}
                            <span className="ml-2">{supplierStatusLabel(s.status)}</span>
                            {s.availableQty != null && (
                              <span className="ml-1">({s.availableQty})</span>
                            )}
                            {s.leadDays != null && (
                              <span className="ml-2">
                                {t('catalog.lead', { days: s.leadDays })}
                              </span>
                            )}
                            {s.cutoffTime && (
                              <span className={`ml-2 ${s.cutoffPassed ? 'text-warning' : ''}`}>
                                {t('catalog.cutoff', { time: s.cutoffTime })}
                                {s.cutoffPassed && t('catalog.cutoffPassed')}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
